
import { Portfolio } from '@/types/portfolio';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building, Droplets, Box, FileText, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

interface PortfolioCardProps {
  portfolio: Portfolio;
  onDelete: (id: string) => void;
  basePath?: string; // allows overriding base path (e.g., /gs-cert-client)
}

const getPortfolioTypeIcon = (type: string) => {
  switch (type) {
    case 'buildings':
      return <Building className="h-5 w-5 mr-1" />;
    case 'formulated_products':
      return <Droplets className="h-5 w-5 mr-1" />;
    case 'interior_spaces':
      return <Box className="h-5 w-5 mr-1" />;
    case 'paper_rolls':
      return <FileText className="h-5 w-5 mr-1" />;
    default:
      return <Box className="h-5 w-5 mr-1" />;
  }
};

export const PortfolioCard = ({ portfolio, onDelete, basePath = '/client' }: PortfolioCardProps) => {
  const typeLabel = portfolio.portfolio_type.replace('_', ' ');
  
  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex justify-between items-start">
          <CardTitle className="text-xl">{portfolio.title}</CardTitle>
          <Badge variant={portfolio.status === 'active' ? 'default' : 'outline'}>
            {portfolio.status}
          </Badge>
        </div>
        <CardDescription className="flex items-center text-sm">
          {getPortfolioTypeIcon(portfolio.portfolio_type)}
          <span className="capitalize">{typeLabel}</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-grow">
        <p className="text-sm text-muted-foreground">
          {portfolio.description || 'No description provided.'}
        </p>
        <p className="text-xs text-muted-foreground mt-4">
          Updated {formatDistanceToNow(new Date(portfolio.updated_at), { addSuffix: true })}
        </p>
      </CardContent>
      <CardFooter className="flex justify-between pt-2 border-t">
        <Button variant="secondary" size="sm" asChild>
          <Link to={`${basePath}/portfolios/${portfolio.id}`}>View Details</Link>
        </Button>
        <Button 
          variant="outline" 
          size="icon" 
          className="text-destructive"
          onClick={() => onDelete(portfolio.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
};
